import { useEffect, useRef, useState } from "react";

import SignatureCanvas from "react-signature-canvas";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

import { StepShell } from "./StepShell";

import { Eraser, LockKeyhole, AlertCircle } from "lucide-react";

import portalApi from "@/lib/portalApi";

export default function DeclarationStep({
  state,
  setStep,
  token,
  onSubmitted,
  applicationEditable,
}) {
  const applicantName =
    state?.personal_details?.fullName ||
    state?.personal_details?.applicant_name ||
    "";

  /*
   * --------------------------------------------------------------------------
   * DECLARATION STATE
   * --------------------------------------------------------------------------
   */

  const declarationResubmissionRequired = Boolean(
    state?.declaration_resubmission_required ??
    state?.portal?.declaration_resubmission_required,
  );

  const declarationLocked =
    state?.declaration_locked !== undefined
      ? Boolean(state.declaration_locked)
      : Boolean(
          state?.portal?.submitted_at && !declarationResubmissionRequired,
        );

  const resubmissionReason =
    state?.declaration_resubmission_reason ??
    state?.portal?.declaration_resubmission_reason ??
    null;

  /*
   * A declaration resubmission request OVERRIDES application editability.
   *
   * Normally:
   *   applicationEditable && !declarationLocked
   *
   * During resubmission:
   *   declarationResubmissionRequired
   *
   * This is intentional. The client must be able to resubmit the declaration
   * even if the application has subsequently reached a normally non-editable
   * CRM status.
   */
  const canEditDeclaration =
    declarationResubmissionRequired ||
    (applicationEditable && !declarationLocked);

  /*
   * --------------------------------------------------------------------------
   * FORM STATE
   * --------------------------------------------------------------------------
   */

  const [tab, setTab] = useState("typed");
  const [typedName, setTypedName] = useState(applicantName);
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const sigRef = useRef(null);

  /*
   * --------------------------------------------------------------------------
   * EFFECTS
   * --------------------------------------------------------------------------
   */

  useEffect(() => {
    if (!typedName && applicantName) {
      setTypedName(applicantName);
    }
  }, [applicantName, typedName]);

  /*
   * When a new resubmission request arrives, start with a clean declaration
   * acceptance state and the typed-signature tab.
   */
  useEffect(() => {
    if (declarationResubmissionRequired) {
      setAccepted(false);
      setTab("typed");
      sigRef.current?.clear();
    }
  }, [declarationResubmissionRequired]);

  /*
   * --------------------------------------------------------------------------
   * SUBMIT
   * --------------------------------------------------------------------------
   */

  const getSubmissionLocation = () => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      return {
        status: "not_supported",
        latitude: null,
        longitude: null,
        accuracy: null,
        captured_at: null,
      };
    }

    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            status: "granted",

            latitude: position.coords.latitude,

            longitude: position.coords.longitude,

            accuracy: position.coords.accuracy ?? null,

            captured_at: new Date().toISOString(),
          });
        },
        (error) => {
          let status = "error";

          switch (error.code) {
            case error.PERMISSION_DENIED:
              status = "denied";
              break;

            case error.POSITION_UNAVAILABLE:
              status = "unavailable";
              break;

            case error.TIMEOUT:
              status = "timeout";
              break;

            default:
              status = "error";
          }

          resolve({
            status,

            latitude: null,

            longitude: null,

            accuracy: null,

            captured_at: null,
          });
        },
        {
          enableHighAccuracy: true,

          timeout: 10000,

          maximumAge: 0,
        },
      );
    });
  };

  const submit = async () => {
    if (!canEditDeclaration || submitting) {
      return;
    }

    if (!accepted) {
      toast.error("Please accept the declaration.");
      return;
    }

    let payload;

    if (tab === "typed") {
      if (!typedName.trim()) {
        toast.error("Please type your full name.");
        return;
      }

      payload = {
        signature_type: "typed",
        signature_data: typedName.trim(),
        accepted: true,
      };
    } else {
      if (!sigRef.current || sigRef.current.isEmpty()) {
        toast.error("Please draw your signature.");
        return;
      }

      const canvas = sigRef.current.getCanvas();

      if (!canvas) {
        toast.error("Unable to capture your signature.");
        return;
      }

      const dataUrl = canvas.toDataURL("image/png");

      payload = {
        signature_type: "drawn",
        signature_data: dataUrl,
        accepted: true,
      };
    }

    const location = await getSubmissionLocation();

    setSubmitting(true);

    try {
      const result = await portalApi.submit(token, {
        ...payload,
        location,
      });

      toast.success(
        declarationResubmissionRequired
          ? "Declaration resubmitted successfully."
          : "Declaration submitted successfully.",
      );

      onSubmitted?.(result);
    } catch (error) {
      console.error("Declaration submission failed:", error);

      const message =
        error?.data?.message ||
        error?.data?.error ||
        error?.data?.detail ||
        error?.message ||
        "Please complete the declaration and try again.";

      toast.error("Declaration submission failed", {
        description: message,
      });
    } finally {
      setSubmitting(false);
    }
  };

  /*
   * --------------------------------------------------------------------------
   * LOCKED DECLARATION
   * --------------------------------------------------------------------------
   *
   * This branch is reached only when:
   *
   *   - the declaration is locked, AND
   *   - there is no active resubmission request.
   *
   * If resubmission is requested, canEditDeclaration is true even when the
   * application itself is no longer editable.
   */

  if (!canEditDeclaration) {
    return (
      <StepShell
        testId="portal-declaration"
        title="Declaration & Digital Signature"
        subtitle="Your declaration has already been submitted."
        onBack={() => setStep(6)}
        onNext={undefined}
        nextLabel={undefined}
        disableNext
      >
        <div className="rounded-2xl border border-green-200 bg-green-50 p-5">
          <div className="flex items-start gap-3">
            <LockKeyhole className="h-5 w-5 text-green-700 mt-0.5" />

            <div>
              <div className="font-semibold text-green-900">
                Declaration submitted
              </div>

              <p className="mt-1 text-sm text-green-800">
                Your declaration and digital signature have been recorded
                successfully and can no longer be edited.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-5 rounded-2xl border border-gray-200 bg-[#F8FAFC] p-5">
          <div className="text-xs uppercase tracking-[0.14em] font-bold text-gray-500 mb-3">
            Declaration
          </div>

          <div className="text-sm text-gray-700 leading-relaxed">
            I hereby confirm that all documents uploaded and information
            provided are genuine and true to the best of my knowledge. I
            authorize <strong className="text-[#0F2D52]">Money Mart</strong> to
            share these documents with partner Banks/NBFCs for the purpose of
            processing my loan application. I understand that Money Mart is a
            Bank Channel Partner (DSA) and does not sanction loans directly; the
            final decision rests solely with the respective lending institution.
          </div>
        </div>

        <div className="mt-5 rounded-2xl border border-gray-200 bg-white p-5">
          <div className="text-xs uppercase tracking-[0.14em] font-bold text-gray-500 mb-2">
            Submitted
          </div>

          <div className="text-sm text-gray-700">
            {state?.portal?.declaration_accepted_at
              ? new Date(state.portal.declaration_accepted_at).toLocaleString()
              : "Submitted"}
          </div>

          {state?.portal?.signature_type && (
            <div className="mt-2 text-sm text-gray-600">
              Signature type:{" "}
              <span className="font-medium capitalize">
                {state.portal.signature_type}
              </span>
            </div>
          )}
        </div>
      </StepShell>
    );
  }

  /*
   * --------------------------------------------------------------------------
   * EDITABLE / RESUBMISSION DECLARATION
   * --------------------------------------------------------------------------
   */

  return (
    <StepShell
      testId="portal-declaration"
      title="Declaration & Digital Signature"
      subtitle={
        declarationResubmissionRequired
          ? "Money Mart has requested that you resubmit your declaration."
          : "Review the declaration carefully, accept the declaration and provide your digital signature to submit your application. Once submitted, the declaration cannot be changed unless Money Mart requests resubmission."
      }
      onBack={() => setStep(6)}
      onNext={submit}
      nextLabel={
        submitting
          ? "Submitting…"
          : declarationResubmissionRequired
            ? "Resubmit Declaration"
            : "Submit Application"
      }
      disableNext={!accepted || submitting}
    >
      {declarationResubmissionRequired && (
        <div className="mb-5 rounded-2xl border border-amber-200 bg-amber-50 p-5">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-amber-700 mt-0.5" />

            <div>
              <div className="font-semibold text-amber-900">
                Declaration resubmission required
              </div>

              <p className="mt-1 text-sm text-amber-800">
                Money Mart has requested that you review and resubmit your
                declaration.
              </p>

              {resubmissionReason && (
                <div className="mt-3 rounded-xl border border-amber-200 bg-white/70 p-3">
                  <div className="text-[11px] uppercase tracking-[0.12em] font-bold text-amber-700">
                    Reason
                  </div>

                  <p className="mt-1 text-sm text-amber-900">
                    {resubmissionReason}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-gray-200 bg-[#F8FAFC] p-5 text-sm text-gray-700 leading-relaxed">
        I hereby confirm that all documents uploaded and information provided
        are genuine and true to the best of my knowledge. I authorize{" "}
        <strong className="text-[#0F2D52]">Money Mart</strong> to share these
        documents with partner Banks/NBFCs for the purpose of processing my loan
        application. I understand that Money Mart is a Bank Channel Partner
        (DSA) and does not sanction loans directly; the final decision rests
        solely with the respective lending institution.
      </div>

      <label
        className="mt-5 flex items-start gap-3 cursor-pointer"
        data-testid="declaration-accept"
      >
        <Checkbox
          checked={accepted}
          onCheckedChange={(value) => setAccepted(Boolean(value))}
          className="mt-0.5 border-gray-400 data-[state=checked]:bg-[#0F2D52] data-[state=checked]:border-[#0F2D52]"
        />

        <span className="text-sm text-gray-700">
          I have read and accept the declaration above.
        </span>
      </label>

      <div className="mt-8">
        <div className="text-xs uppercase tracking-[0.14em] font-bold text-gray-500 mb-3">
          Your signature
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="rounded-full">
            <TabsTrigger value="typed" className="rounded-full text-xs">
              Type name
            </TabsTrigger>

            <TabsTrigger value="drawn" className="rounded-full text-xs">
              Draw signature
            </TabsTrigger>
          </TabsList>

          <TabsContent value="typed">
            <Input
              data-testid="sig-typed"
              value={typedName}
              onChange={(event) => setTypedName(event.target.value)}
              placeholder="Your full name as signature"
              className="h-12 rounded-xl font-heading text-xl italic text-[#0F2D52]"
            />

            <p className="mt-2 text-[11px] text-gray-500">
              Your typed name will be treated as your digital signature on this
              application.
            </p>
          </TabsContent>

          <TabsContent value="drawn">
            <div className="rounded-2xl border border-gray-300 bg-white overflow-hidden">
              <SignatureCanvas
                ref={sigRef}
                penColor="#0F2D52"
                canvasProps={{
                  className: "w-full h-40",
                  "data-testid": "sig-canvas",
                }}
              />
            </div>

            <div className="mt-2 flex justify-end">
              <Button
                size="sm"
                variant="ghost"
                type="button"
                onClick={() => sigRef.current?.clear()}
              >
                <Eraser className="h-4 w-4 mr-2" />
                Clear
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </StepShell>
  );
}
